/**
 * Calculates the specified percentile from an array of numerical values.
 *
 * Percentile Definition : a value on a scale of one hundred that indicates the percent
 * of a distribution that is equal to or below it eg a percentile score of 95
 * is a score equal to or better than 95 percent of the scores
 *
 *
 * @param values - Array of numerical values (typically width measurements in pixels)
 * @param percentile - Percentile to calculate (0-100, where 90 = 90th percentile)
 * @returns The value at the specified percentile, or 0 if no values provided
 *
 * @example
 * const widths = [120, 150, 180, 200, 350]; // 350 is an outlier
 * const p90 = calculatePercentile(widths, 90); // Returns 200
 * const median = calculatePercentile(widths, 50); // Returns 180
 */
export function calculatePercentile(values: number[], percentile: number): number {
  if (values.length === 0) return 0;
  const clampedPercentile = Math.max(0, Math.min(100, percentile)); // Clamp to [0, 100] to prevent undefined array access
  const sorted = [...values].sort((a, b) => a - b); // Shallow copy before in-place sort to avoid mutating input array
  const index = Math.floor((clampedPercentile / 100) * (sorted.length - 1));
  return sorted[index];
}

/**
 * Computes the optimal column width from collected content width samples.
 *
 * This is the core function for data grid auto-sizing.
 *
 * Strategy comparison:
 * - 'p90' (recommended): Uses 90th percentile, accommodates most content while avoiding
 *   extreme outliers that could make columns unnecessarily wide
 * - 'max': Uses maximum width, ensures all content fits but may create very wide
 *   columns if there are outliers
 *
 * Auto-sizing workflow:
 * 1. Cell content is measured during initial render
 * 2. Width samples are collected from multiple rows (up to MAX_SAMPLE_ROWS)
 * 3. This function analyzes samples to determine optimal width
 * 4. Result is used to set column width
 *
 * @param samples - Array of width measurements in pixels from cell content
 * @param minWidth - Minimum allowed column width (fallback for empty samples)
 * @param strategy - Algorithm to use: 'p90' for 90th percentile, 'max' for maximum
 * @returns Calculated optimal width in pixels, or minWidth if no samples
 *
 * @example
 * const cellWidths = [120, 125, 130, 128, 200]; // 200 is wider content
 * const width = computePreferredWidth(cellWidths, 100, 'p90'); // Returns ~130
 * const maxWidth = computePreferredWidth(cellWidths, 100, 'max'); // Returns 200
 */
export function computePreferredWidth(samples: number[], minWidth: number, strategy: 'p90' | 'max' = 'p90'): number {
  if (samples.length === 0) {
    return minWidth; // Fallback to minWidth when no samples
  }
  return strategy === 'p90' ? calculatePercentile(samples, 90) : Math.max(...samples);
}
